import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useT } from "@/i18n/LanguageContext";
import { STAGE_LABEL, STAGE_BADGE, PRODUCTION_SUBSTAGE, PRODUCTION_SUBSTAGE_BADGE, productionSubstage, productionDeadline, packedStageOverride } from "@/constants/roles";
import { Badge } from "@/components/ui/badge";
import { UsFlag } from "@/components/UsFlag";
import { PickupBadge } from "@/components/PickupBadge";
import { PriorityTag } from "@/components/PriorityTag";
import { ColorDot } from "@/components/ColorSwatch";
import { effectiveColor, CustomColorNote } from "@/components/CustomColorNote";
import { displayCode } from "@/constants/coverCodes";

const STAGE_ORDER = ["lead", "pre_order", "pending", "advance", "flagged", "na", "production", "product_transfer",
  "inventory_packing", "packing", "shipping", "completed"];
const BADGE_CLS = {
  neutral: "bg-slate-100 text-slate-700 border-slate-200",
  blue: "bg-blue-50 text-blue-700 border-blue-200",
  warning: "bg-amber-50 text-amber-700 border-amber-200",
  success: "bg-emerald-50 text-emerald-700 border-emerald-200",
  critical: "bg-red-50 text-red-700 border-red-200",
};
// 3 urgent · 2 top · 1 highlight (any combination of flags is valid — the strongest wins)
export function rushLevel(o) {
  if (o.rush_urgent || Number(o.rush_level || 0) >= 3) return 3;
  if (o.rush_top || Number(o.rush_level || 0) >= 2) return 2;
  if (o.rush_highlight || Number(o.rush_level || 0) >= 1) return 1;
  return 0;
}
const LEVELS = [
  { key: 3, label: "URGENT", icon: "🚩", cls: "bg-red-600 text-white border-red-700", row: "border-red-300 bg-red-100/80 hover:bg-red-100" },
  { key: 2, label: "TOP", icon: "⬆", cls: "bg-orange-500 text-white border-orange-600", row: "border-orange-200 bg-orange-50 hover:bg-orange-100/70" },
  { key: 1, label: "HIGHLIGHT", icon: "✦", cls: "bg-red-100 text-red-800 border-red-300", row: "border-slate-100 hover:bg-slate-50" },
];
const levelMeta = (lvl) => LEVELS.find((l) => l.key === lvl);
const stageRank = (s) => { const i = STAGE_ORDER.indexOf(s || ""); return i === -1 ? 999 : i; };
const fmtDate = (iso) => (iso ? String(iso).slice(0, 10) : "");

function LevelBadge({ o, t }) {
  const m = levelMeta(rushLevel(o));
  if (!m) return null;
  const parts = [o.rush_urgent && "Urgent", o.rush_top && "Top", o.rush_highlight && "Highlight"].filter(Boolean).join(" + ");
  return (
    <Badge className={`${m.cls} border font-black text-[10px] uppercase tracking-widest whitespace-nowrap`} title={parts} data-testid={`rush-level-${o.id}`}>
      {m.icon} {t(m.label)}
    </Badge>
  );
}

function StageBadge({ o, t }) {
  if (o.waiting_reply) {
    return <Badge className="bg-violet-600 text-white border border-violet-700 font-bold text-[11px]" data-testid={`rush-stage-${o.id}`}>⏳ {t("WAITING FOR REPLY")}</Badge>;
  }
  const ov = packedStageOverride(o);
  if (ov) return <Badge className={`${ov.badge} font-semibold text-[11px]`} data-testid={`rush-stage-${o.id}`}>{ov.icon} {t(ov.label)}</Badge>;
  if (o.status === "production") {
    const sub = productionSubstage(o);
    return <Badge className={`${PRODUCTION_SUBSTAGE_BADGE[sub]} text-[11px]`} data-testid={`rush-stage-${o.id}`}>{t(PRODUCTION_SUBSTAGE[sub])}</Badge>;
  }
  return <Badge className={`${BADGE_CLS[STAGE_BADGE[o.status]] || BADGE_CLS.neutral} border text-[11px]`} data-testid={`rush-stage-${o.id}`}>{t(STAGE_LABEL[o.status] || o.status)}</Badge>;
}

function Deadline({ o, t }) {
  if (o.status !== "production") return <span className="text-slate-300">—</span>;
  const d = productionDeadline(o);
  if (!d) return <span className="text-slate-300">—</span>;
  const days = Math.ceil((d.getTime() - Date.now()) / 86_400_000);
  const cls = days < 0 ? "text-red-700 font-bold" : days <= 1 ? "text-amber-700 font-semibold" : "text-slate-700";
  const label = days < 0 ? `${Math.abs(days)}d ${t("overdue")}` : days === 0 ? t("due today") : `${days} ${t("days left")}`;
  return <span className={`whitespace-nowrap ${cls}`} data-testid={`rush-deadline-${o.id}`} title={d.toISOString().slice(0, 10)}>{label}</span>;
}

function Chip({ active, onClick, children, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
            className={`px-3 py-1 rounded-full border text-xs font-semibold transition-colors ${active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"}`}>
      {children}
    </button>
  );
}

function RushRow({ o, t, lang, stageTo, onOpen }) {
  const m = levelMeta(rushLevel(o));
  const veh = [o.year, o.brand, o.model, o.trim].filter(Boolean).join(" ");
  const ec = effectiveColor(o);
  const color = ec.color;
  return (
    <tr className={`border-t cursor-pointer ${m?.row || ""}`} onClick={onOpen} data-testid={`rush-row-${o.id}`}>
      <td className="px-4 py-3"><LevelBadge o={o} t={t} /></td>
      <td className={`px-4 py-3 font-mono font-semibold ${rushLevel(o) >= 1 ? "text-red-700" : "text-slate-900"}`}>
        <div className="flex items-center gap-2 flex-wrap">
          <Link to={`/order/${o.id}`} state={{ from: "rush" }} className="hover:underline" data-testid={`rush-po-${o.id}`}>{o.po_number}</Link>
          <UsFlag order={o} />
          <PickupBadge order={o} />
          <PriorityTag order={o} />
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2 flex-wrap">
          <StageBadge o={o} t={t} />
          {stageTo && (
            <Link to={stageTo} className="text-[11px] text-slate-500 hover:text-slate-900 underline-offset-2 hover:underline whitespace-nowrap"
                  title={t("Open stage list")} data-testid={`rush-stage-link-${o.id}`}>{t(STAGE_LABEL[o.status] || o.status)} →</Link>
          )}
        </div>
      </td>
      <td className="px-4 py-3 text-sm text-slate-800 whitespace-nowrap">{o.customer?.name || <span className="text-slate-400">—</span>}</td>
      <td className="px-4 py-3 text-sm text-slate-800 whitespace-nowrap">{veh || <span className="text-slate-400">—</span>}</td>
      <td className="px-4 py-3 text-sm text-slate-800 whitespace-nowrap">
        <span className="inline-flex items-center gap-1.5">{color && <ColorDot color={color} size={14} />}{color || "—"}<CustomColorNote ec={ec} testid={`rush-color-note-${o.id}`} /></span>
      </td>
      <td className="px-4 py-3 font-mono text-xs text-slate-700 whitespace-nowrap">
        {o.front_cover_code ? (
          <div className="leading-tight">
            <div><span className="text-slate-400 mr-1">F</span>{displayCode(o.front_cover_code, "front", lang)}</div>
            <div><span className="text-slate-400 mr-1">R</span>{o.rear_cover_code ? displayCode(o.rear_cover_code, "rear", lang) : "—"}</div>
          </div>
        ) : "—"}
      </td>
      <td className="px-4 py-3 text-xs text-slate-700 whitespace-nowrap uppercase">{(o.assigned_factory || "factory_1").replace("factory_", "F")}</td>
      <td className="px-4 py-3 text-xs text-slate-600 whitespace-nowrap" data-testid={`rush-set-${o.id}`}>
        <div>{fmtDate(o.rush_set_at) || "—"}</div>
        {o.rush_set_by && <div className="text-slate-400 truncate max-w-[160px]" title={o.rush_set_by}>{o.rush_set_by}</div>}
      </td>
      <td className="px-4 py-3 text-xs"><Deadline o={o} t={t} /></td>
    </tr>
  );
}

export default function RushQueue() {
  const { user } = useAuth();
  const { t, lang } = useT();
  const navigate = useNavigate();
  const [level, setLevel] = useState(0);
  const [stage, setStage] = useState("");
  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["orders", "rush"],
    queryFn: async () => (await api.get("/orders", { params: { status: "rush" } })).data,
    refetchInterval: 30_000,
  });
  const sorted = useMemo(() => [...rows].sort((a, b) => {
    const d = rushLevel(b) - rushLevel(a);
    if (d !== 0) return d;
    const s = stageRank(a.status) - stageRank(b.status);
    if (s !== 0) return s;
    return (a.rush_set_at || "").localeCompare(b.rush_set_at || "");
  }), [rows]);
  const levelCounts = useMemo(() => sorted.reduce((acc, o) => { acc[rushLevel(o)] = (acc[rushLevel(o)] || 0) + 1; return acc; }, {}), [sorted]);
  const stages = useMemo(() => {
    const c = {};
    sorted.forEach((o) => { c[o.status] = (c[o.status] || 0) + 1; });
    return Object.keys(c).sort((a, b) => stageRank(a) - stageRank(b)).map((k) => [k, c[k]]);
  }, [sorted]);
  const visible = sorted.filter((o) => (!level || rushLevel(o) === level) && (!stage || o.status === stage));
  const sidebarStages = new Set((user?.role === "super_admin" || user?.role === "back_office")
    ? STAGE_ORDER : ["pending", "advance", "production", "product_transfer", "completed"]);
  const stageTo = (o) => (sidebarStages.has(o.status) ? `/orders/${o.status}` : null);
  const openOrder = (o) => (e) => {
    if (e.target.closest("a, button")) return;
    navigate(`/order/${o.id}`, { state: { from: "rush" } });
  };

  return (
    <div className="p-6 space-y-4" data-testid="rush-queue-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">🚩 {t("Rush queue")} <span className="text-slate-400 font-normal text-base" data-testid="rush-count">· {sorted.length} {t("rushed orders")}</span></h1>
          <p className="text-sm text-slate-500">{t("Every rushed order across all stages — Urgent first, then Top, then Highlight.")}</p>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="rush-level-filters">
          <Chip active={!level} onClick={() => setLevel(0)} testid="rush-filter-all">{t("All levels")} · {sorted.length}</Chip>
          {LEVELS.map((l) => (
            <Chip key={l.key} active={level === l.key} onClick={() => setLevel(level === l.key ? 0 : l.key)} testid={`rush-filter-${l.key}`}>
              {l.icon} {t(l.label)} · {levelCounts[l.key] || 0}
            </Chip>
          ))}
        </div>
      </div>
      {stages.length > 1 && (
        <div className="flex flex-wrap gap-2" data-testid="rush-stage-filters">
          <Chip active={!stage} onClick={() => setStage("")} testid="rush-stage-all">{t("All stages")}</Chip>
          {stages.map(([k, n]) => (
            <Chip key={k} active={stage === k} onClick={() => setStage(stage === k ? "" : k)} testid={`rush-stage-filter-${k}`}>{t(STAGE_LABEL[k] || k)} · {n}</Chip>
          ))}
        </div>
      )}
      {isLoading && <div className="text-slate-500 text-sm">{t("Loading…")}</div>}
      {!isLoading && sorted.length === 0 && (
        <div className="rounded-lg border border-dashed border-slate-300 p-10 text-center text-slate-400" data-testid="rush-empty">{t("No rushed orders right now.")}</div>
      )}
      {!isLoading && sorted.length > 0 && visible.length === 0 && (
        <div className="rounded-lg border border-dashed border-slate-300 p-10 text-center text-slate-400" data-testid="rush-empty-filtered">{t("No rushed orders match this filter.")}</div>
      )}
      {visible.length > 0 && (
        <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
          <table className="w-full text-left" data-testid="rush-table">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-2">{t("Level")}</th>
                <th className="px-4 py-2">PO #</th>
                <th className="px-4 py-2">{t("Stage")}</th>
                <th className="px-4 py-2">{t("Customer")}</th>
                <th className="px-4 py-2">{t("Vehicle")}</th>
                <th className="px-4 py-2">{t("Colour")}</th>
                <th className="px-4 py-2">{t("Codes")}</th>
                <th className="px-4 py-2">{t("Factory")}</th>
                <th className="px-4 py-2">{t("Rush set")}</th>
                <th className="px-4 py-2">{t("Deadline")}</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((o) => <RushRow key={o.id} o={o} t={t} lang={lang} stageTo={stageTo(o)} onOpen={openOrder(o)} />)}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
